import {
  AdvancedSearchMode,
  AdvancedSearchPreviewCard,
  ADVANCED_SEARCH_MODE_LABELS
} from './advanced-search';
import { CARD_PURCHASE_STATUS, CARD_TYPE, ModelCardRenderedSelection } from './card';
import { ICardCollection } from './collection';

export const ADVANCED_SEARCH_SESSION_COLLECTION_ID_PREFIX = 'advanced-search-session:';

export function isAdvancedSearchSessionCollectionId(collectionId: string | null | undefined): collectionId is string {
  return !!collectionId && collectionId.startsWith(ADVANCED_SEARCH_SESSION_COLLECTION_ID_PREFIX);
}

export function createAdvancedSearchSessionCollection(
  cards: AdvancedSearchPreviewCard[],
  searchPrompt: string,
  searchMode: AdvancedSearchMode,
): ICardCollection | null {
  const successCards = cards.filter((card) => card.status === 'success');
  if (successCards.length === 0) {
    return null;
  }

  const sessionId = createSessionCollectionId();
  const collectionId = `${ADVANCED_SEARCH_SESSION_COLLECTION_ID_PREFIX}${sessionId}`;
  const timestamp = new Date().toISOString();
  const collectionTitle = buildAdvancedSearchSessionCollectionTitle(
    searchPrompt,
    searchMode,
    successCards.length,
  );
  const collectionDescription = buildAdvancedSearchSessionCollectionDescription(
    successCards,
    searchPrompt,
    searchMode,
  );
  const collectionCards = successCards.map((card, index) =>
    createAdvancedSearchSessionCollectionCard(card, index, collectionId, timestamp, searchMode)
  );
  const firstCard = collectionCards[0];

  return {
    collectionId,
    collectionSharingId: collectionId,
    collectionTitle,
    collectionContentMobile: firstCard?.cardContentMobile || '',
    collectionPosterMobile: firstCard?.cardPosterMobile || '',
    collectionContentWeb: firstCard?.cardContentWeb || '',
    collectionPosterWeb: firstCard?.cardPosterWeb || '',
    collectionAuthor: 'AI Preview',
    collectionDescription,
    collectionCards,
    accessType: 'Private',
    created: timestamp,
    updated: timestamp,
  };
}

function createAdvancedSearchSessionCollectionCard(
  card: AdvancedSearchPreviewCard,
  index: number,
  collectionId: string,
  timestamp: string,
  fallbackSearchMode: AdvancedSearchMode,
): ModelCardRenderedSelection {
  const fallbackId = `advanced-search-session-card-${index + 1}`;
  const cardId = `${collectionId}:${card.id || fallbackId}`;
  const portraitSource = card.pairPortraitImageUrl
    || card.pairPortraitImageDataUrl
    || card.thumbnailImageDataUrl
    || card.imageDataUrl
    || '';
  const landscapeSource = card.pairLandscapeImageUrl
    || card.viewerImageUrl
    || card.pairLandscapeImageDataUrl
    || card.viewerImageDataUrl
    || card.imageDataUrl
    || '';

  return {
    id: cardId,
    cardSharingId: cardId,
    publicModelId: cardId,
    cardContentMobile: '',
    cardPosterMobile: portraitSource,
    cardContentWeb: '',
    cardPosterWeb: landscapeSource,
    cardModelLink: '',
    cardType: CARD_TYPE.MODEL,
    cardCategory: ADVANCED_SEARCH_MODE_LABELS[card.searchMode || fallbackSearchMode],
    cardAuthor: 'AI Preview',
    cardTitle: card.title,
    cardDescription: '',
    cardPrice: 0,
    parentCollectionId: collectionId,
    purchaseStatus: CARD_PURCHASE_STATUS.DRAFT_NOT_LIKED,
    modifiedDate: timestamp,
    visible: false,
    isFullscreen: false,
    cardShortView: false,
    isVideoShown: false,
    isPosterShown: true,
    canBePlayed: true,
    posterFit: 'contain',
  };
}

function createSessionCollectionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildAdvancedSearchSessionCollectionTitle(
  searchPrompt: string,
  searchMode: AdvancedSearchMode,
  cardsCount: number,
): string {
  const normalizedPrompt = normalizeSingleLineText(searchPrompt);
  if (normalizedPrompt) {
    return truncateText(normalizedPrompt, 64);
  }

  return `${ADVANCED_SEARCH_MODE_LABELS[searchMode]} · ${cardsCount} cards`;
}

function buildAdvancedSearchSessionCollectionDescription(
  cards: AdvancedSearchPreviewCard[],
  searchPrompt: string,
  searchMode: AdvancedSearchMode,
): string {
  const extractedConcepts = cards
    .map((card) => extractCardConceptSnippet(card))
    .map((snippet) => normalizeSingleLineText(snippet))
    .filter((snippet): snippet is string => !!snippet);
  const uniqueConcepts = Array.from(new Set(extractedConcepts));

  if (uniqueConcepts.length > 0) {
    return truncateText(uniqueConcepts[0], 140);
  }

  const titles = cards
    .map((card) => normalizeSingleLineText(card.title))
    .filter((title): title is string => !!title)
    .slice(0, 3);

  if (titles.length > 0) {
    return truncateText(`AI variations: ${titles.join(', ')}`, 140);
  }

  const normalizedPrompt = normalizeSingleLineText(searchPrompt);
  if (normalizedPrompt) {
    return truncateText(`AI-generated variations for "${normalizedPrompt}".`, 140);
  }

  return `AI-generated ${ADVANCED_SEARCH_MODE_LABELS[searchMode].toLowerCase()} collection.`;
}

function extractCardConceptSnippet(card: AdvancedSearchPreviewCard): string {
  const promptUsed = `${card.promptUsed || ''}`;
  const conceptMatch = promptUsed.match(/Asset concept:\s*([^\n\r]+)/i);
  if (conceptMatch?.[1]) {
    return conceptMatch[1];
  }

  return `${card.imagePrompt || ''}`;
}

function normalizeSingleLineText(value: string): string {
  return `${value || ''}`.replace(/\s+/g, ' ').trim();
}

function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  const truncated = value.slice(0, maxLength).trimEnd();
  return `${truncated}…`;
}
